const { fetchSingle: fetchSingleSection, convertToDOM } =
  window.BAO.sectionRenderer

const ROW_SECTION = 'back-in-stock-recommendation'
const TRIGGER_SELECTOR = '[data-back-in-stock-external-el="trigger"]'
const NOSTO_TIMEOUT_MS = 4000

const rowCache = new Map()

function productPath (productUrl) {
  if (!productUrl) return ''

  let path

  try {
    path = new URL(productUrl, window.location.origin).pathname
  } catch (error) {
    return ''
  }

  const root = window.Shopify?.routes?.root || '/'

  if (!path.startsWith(root)) {
    path = `${root}${path.replace(/^\//, '')}`
  }

  return path
}

async function fetchRow (url) {
  const path = productPath(url)
  if (!path) return null

  if (!rowCache.has(path)) {
    const html = await fetchSingleSection(ROW_SECTION, path)
    const row = convertToDOM(html).querySelector(ROW_SECTION)

    if (!row) return null

    rowCache.set(path, row)
  }

  return rowCache.get(path).cloneNode(true)
}

function rowVariants (row) {
  try {
    return JSON.parse(
      row.querySelector('[data-back-in-stock-recommendation-el="variants"]')
        .textContent,
    )
  } catch (error) {
    return []
  }
}

function isShoppable (variant) {
  return variant.available && !variant.preorder && !variant.soldOut
}

class BackInStockRecommendations extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['items']
  }

  static get requiredAttributes () {
    return ['data-placement-id']
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(document, 'click', this.handleTrigger.bind(this))
  }

  handleTrigger (event) {
    const triggerEl = event.target.closest(TRIGGER_SELECTOR)
    if (!triggerEl) return

    if (this.contains(triggerEl)) {
      this.closest('.drw-Drawer_Inner')?.scrollTo({ top: 0 })
    }

    this.load({
      productId: triggerEl.dataset.productId,
      sizeOption: triggerEl.dataset.sizeOption,
      size: triggerEl.dataset.variantSize,
    })
  }

  async load ({ productId, sizeOption, size }) {
    const request = Symbol('request')
    this.request = request

    this.setAttribute('aria-hidden', 'true')
    this.els.items.element.replaceChildren()

    if (!productId || !sizeOption || !size) return
    if (typeof window.nostojs !== 'function') return

    try {
      const products = await this.loadPlacement(productId)
      const rows = await Promise.all(
        products.map((product) => fetchRow(product.url).catch(() => null)),
      )

      if (this.request !== request) return

      const matches = rows
        .filter(
          (row) =>
            row &&
            row.dataset.productId !== `${productId}` &&
            row.dataset.delisted !== 'true' &&
            row.dataset.sizeOption === sizeOption &&
            rowVariants(row).some(
              (variant) => variant.size === size && isShoppable(variant),
            ),
        )
        .slice(0, this.limit)

      if (!matches.length) return

      matches.forEach((row) => {
        row.dataset.size = size
        row.dataset.placementId = this.placementId
      })

      this.els.items.element.replaceChildren(...matches)
      this.setAttribute('aria-hidden', 'false')
    } catch (error) {
      console.error('Failed to load back in stock recommendations', error)
    }
  }

  loadPlacement (productId) {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(
        () => reject(new Error('Nosto did not respond')),
        NOSTO_TIMEOUT_MS,
      )

      window.nostojs((api) => {
        api
          .defaultSession()
          .setResponseMode('JSON_ORIGINAL')
          .viewProduct(`${productId}`)
          .setPlacements([this.placementId])
          .load()
          .then((response) => {
            clearTimeout(timeout)
            resolve(
              response?.campaigns?.recommendations?.[this.placementId]
                ?.products ||
                response?.recommendations?.[this.placementId]?.products ||
                [],
            )
          })
          .catch((error) => {
            clearTimeout(timeout)
            reject(error)
          })
      })
    })
  }

  get placementId () {
    return this.dataset.placementId
  }

  get limit () {
    return parseInt(this.dataset.limit, 10) || 6
  }
}

class BackInStockRecommendation extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['form', 'colour', 'size', 'submit', 'notify']
  }

  connectedCallback () {
    super.connectedCallback()

    this.variants = rowVariants(this)

    if (this.placementId) {
      this.els.form.element.dataset.nostoPlacementId = this.placementId
    }

    this.selectSize(this.dataset.size)
    this.update()
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this.els.form.element,
      'submit',
      this.onSubmit.bind(this),
    )
    this.listeners.add(this.els.size.element, 'change', this.update.bind(this))
    this.listeners.add(
      this.els.colour.element,
      'change',
      this.onColourChange.bind(this),
    )

    if (this.els.learnMore?.element) {
      this.listeners.add(
        this.els.learnMore.element,
        'click',
        this.togglePreorderPopup.bind(this),
      )
    }

    this.listeners.add(this, 'click', (event) => {
      if (event.target.closest('[data-preorder-popup-close]')) {
        this.togglePreorderPopup(false)
      }
    })

    this.watchPurpleDot()
  }

  selectSize (size) {
    const variant = this.variants.find((v) => v.size === size)
    if (variant) this.els.size.element.value = `${variant.id}`
  }

  get selectedVariant () {
    return this.variants.find(
      (variant) => `${variant.id}` === this.els.size.element.value,
    )
  }

  get state () {
    const variant = this.selectedVariant

    if (!variant || variant.soldOut || !variant.available) return 'notify'
    if (variant.preorder) return 'preorder'

    return 'add'
  }

  update () {
    const variant = this.selectedVariant
    const state = this.state
    const formEl = this.els.form.element
    const submitEl = this.els.submit.element
    const notifyEl = this.els.notify.element
    const isPreorder = state === 'preorder'

    submitEl.setAttribute('aria-hidden', state === 'notify')
    submitEl.toggleAttribute('disabled', state === 'notify')
    notifyEl.setAttribute('aria-hidden', state !== 'notify')

    const text = isPreorder ? this.dataset.textPreorder : this.dataset.textAdd

    this.els.submitText.element.dataset.text = text
    submitEl.setAttribute('aria-label', text)

    formEl.dataset.isPreorder = isPreorder ? 'true' : 'false'
    formEl.classList.toggle('is-preorder-variant', isPreorder)
    formEl.classList.toggle('x-pd-atc-form--preorder', isPreorder)

    this.els.preorderNote.element.setAttribute('aria-hidden', !isPreorder)
    if (!isPreorder) this.togglePreorderPopup(false)

    notifyEl.dataset.variantId = variant?.id || ''
    notifyEl.dataset.variantTitle = variant?.title || ''
    notifyEl.dataset.variantSize = variant?.size || ''
    notifyEl.dataset.variantPrice = variant?.price || ''
    notifyEl.dataset.sizeOption = this.dataset.sizeOption || ''

    this.updateColours(variant?.size)
  }

  updateColours (size) {
    Array.from(this.els.colour.element.options).forEach((option) => {
      if (!option.dataset.sizes) return

      option.disabled = !size || !option.dataset.sizes.includes(`|${size}|`)
    })
  }

  async onColourChange () {
    const option = this.els.colour.element.selectedOptions[0]
    const size = this.selectedVariant?.size

    if (!option?.dataset.url) return

    this.classList.add('drw-BackInStockRec-loading')

    try {
      const row = await fetchRow(option.dataset.url)
      if (!row || !this.isConnected) return

      row.dataset.size = size || ''
      row.dataset.placementId = this.placementId || ''

      this.replaceWith(row)
    } catch (error) {
      console.error('Failed to load recommendation colour', error)

      this.els.colour.element.value = this.dataset.productHandle
      this.classList.remove('drw-BackInStockRec-loading')
    }
  }

  async onSubmit (event) {
    event.preventDefault()

    if (this.state === 'notify') return

    await window.BAO.Cart.addItemFromForm(this.els.form.element)
  }

  togglePreorderPopup (force) {
    const popupEl = this.querySelector('[data-preorder-popup]')
    if (!popupEl) return

    const isActive =
      typeof force === 'boolean'
        ? force
        : popupEl.getAttribute('aria-hidden') === 'true'

    popupEl.setAttribute('aria-hidden', !isActive)
    popupEl.classList.toggle('prd-Detail_PreorderPopup-active', isActive)
  }

  watchPurpleDot () {
    const observer = new MutationObserver(() => {
      const dispatchEl = this.querySelector(
        '#pd-waitlist-info #pd-dispatch-date',
      )
      if (
        !dispatchEl ||
        this.els.preorderNoteText.element.querySelector('.prd-Note_Text')
      ) {
        return
      }

      const textEl = document.createElement('p')
      textEl.classList.add('prd-Note_Text')
      textEl.innerText = dispatchEl.innerText

      this.els.preorderNoteText.element.append(textEl)
      observer.disconnect()
    })

    observer.observe(this, { childList: true, subtree: true })
  }

  get placementId () {
    return this.dataset.placementId
  }
}

if (!customElements.get('back-in-stock-recommendations')) {
  customElements.define(
    'back-in-stock-recommendations',
    BackInStockRecommendations,
  )
}

if (!customElements.get('back-in-stock-recommendation')) {
  customElements.define(
    'back-in-stock-recommendation',
    BackInStockRecommendation,
  )
}
